import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const MIN_ONLINE_LEAD_DAYS = 1;
const DELIVERY_TIME_ZONE = 'America/Toronto';
const CALL_TO_ORDER_MESSAGE =
  'Same-day delivery is not available for online orders. Please call us at {phone} to arrange same-day delivery, or choose {min} or later online.';

/**
 * Format date parts as YYYY-MM-DD for date inputs.
 * @param {{ year: number, month: number, day: number }} dateParts
 * @returns {string}
 */
export function formatDateParts(dateParts) {
  const y = dateParts.year;
  const m = String(dateParts.month).padStart(2, '0');
  const d = String(dateParts.day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a Date as YYYY-MM-DD using local date parts for date inputs.
 * @param {Date} date
 * @returns {string}
 */
export function formatLocalDate(date) {
  return formatDateParts({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
}

/**
 * Return the store's current wall-clock date (YYYY-MM-DD) in the delivery timezone.
 * Used to distinguish a true same-day attempt (today) from any other out-of-range date.
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {string}
 */
export function getStoreTodayString(now = new Date(), timeZone = DELIVERY_TIME_ZONE) {
  return formatDateParts(getZonedDateTimeParts(now, timeZone));
}

/**
 * Read wall-clock date/time parts for a Date in the store delivery timezone.
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number }}
 */
export function getZonedDateTimeParts(date, timeZone = DELIVERY_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
  };
}

/**
 * Add calendar days to date parts without depending on the shopper browser timezone.
 * @param {{ year: number, month: number, day: number }} dateParts
 * @param {number} days
 * @returns {{ year: number, month: number, day: number }}
 */
export function addCalendarDays(dateParts, days) {
  const utcDate = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day));
  utcDate.setUTCDate(utcDate.getUTCDate() + days);
  return {
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  };
}

/**
 * Return the local date-input bounds for online delivery.
 *
 * Same-day delivery is only available by phone; the earliest online delivery date
 * is ALWAYS the next calendar day in the store timezone, independent of the store's
 * current time of day. `minLeadDays` can only push the minimum further out.
 *
 * @param {Date} now
 * @param {number} maxDays
 * @param {number} minLeadDays
 * @param {string} timeZone
 * @returns {{ min: string, max: string }}
 */
export function getDeliveryDateBounds(
  now = new Date(),
  maxDays = 30,
  minLeadDays = 0,
  timeZone = DELIVERY_TIME_ZONE
) {
  const shopNow = getZonedDateTimeParts(now, timeZone);
  // Online orders open at the next calendar day, always.
  const effectiveMinDays = Math.max(minLeadDays, MIN_ONLINE_LEAD_DAYS);

  return {
    min: formatDateParts(addCalendarDays(shopNow, effectiveMinDays)),
    max: formatDateParts(addCalendarDays(shopNow, maxDays)),
  };
}

/**
 * Safe default blackout config used when the storefront has supplied no closure
 * data, or when it supplied something unreadable.
 * @type {{ full: Set<string>, slots: Map<string, { slots: Set<string>, description: string|undefined, alternative: string|undefined }>, valid: boolean }}
 */
const EMPTY_BLACKOUT_CONFIG = Object.freeze({ full: new Set(), slots: new Map(), valid: true });

/**
 * Parse a JSON array defensively.
 * Returns { ok, present, value } where `present` is true whenever a non-empty
 * raw value was supplied (so callers can flag a malformed value) and `ok` is
 * true only when a real array was recovered.
 * @param {unknown} raw
 * @returns {{ ok: boolean, present: boolean, value: unknown[] }}
 */
function parseJsonArraySafely(raw) {
  if (raw === undefined || raw === null || raw === '') return { ok: false, present: false, value: [] };

  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { ok: false, present: true, value: [] };
    }
  }
  if (!Array.isArray(value)) return { ok: false, present: true, value: [] };
  return { ok: true, present: true, value };
}

/** Announce an unreadable closure config without breaking the scheduler. */
function reportBlackoutConfigError() {
  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    console.error(
      '[cart-delivery-scheduler] Delivery closure config is malformed; retaining only the readable closures.'
    );
  }
}

/**
 * Turn the storefront's JSON closure config into a lookup structure.
 *
 * `fullRaw` is a JSON array of whole-day closure dates ("YYYY-MM-DD").
 * `slotsRaw` is a JSON array of { date, slots, description?, alternative? } objects
 * describing dates whose specific slot(s) are closed.
 *
 * Malformed input is tolerated: whatever can be read is kept, the rest is dropped,
 * and every valid delivery date still works. A partially readable config is never
 * silently ignored — it is announced on the console.
 * @param {unknown} fullRaw
 * @param {unknown} slotsRaw
 * @returns {{ full: Set<string>, slots: Map<string, { slots: Set<string>, description: string|undefined, alternative: string|undefined }>, valid: boolean }}
 */
export function parseBlackoutConfig(fullRaw, slotsRaw) {
  const config = { full: new Set(), slots: new Map(), valid: true };

  const fullResult = parseJsonArraySafely(fullRaw);
  if (fullResult.ok) {
    for (const value of fullResult.value) {
      if (typeof value === 'string' && value) config.full.add(value);
      else config.valid = false;
    }
  } else if (fullResult.present) {
    config.valid = false;
  }

  const slotsResult = parseJsonArraySafely(slotsRaw);
  if (slotsResult.ok) {
    for (const rawEntry of slotsResult.value) {
      if (!rawEntry || typeof rawEntry !== 'object') {
        config.valid = false;
        continue;
      }
      const entry = /** @type {Record<string, unknown>} */ (rawEntry);
      if (typeof entry.date !== 'string' || !entry.date || !Array.isArray(entry.slots)) {
        config.valid = false;
        continue;
      }
      const slotSet = new Set();
      for (const slot of entry.slots) {
        if (typeof slot === 'string' && slot) slotSet.add(slot);
        else config.valid = false;
      }
      config.slots.set(entry.date, {
        slots: slotSet,
        description: typeof entry.description === 'string' ? entry.description : undefined,
        alternative: typeof entry.alternative === 'string' ? entry.alternative : undefined,
      });
    }
  } else if (slotsResult.present) {
    config.valid = false;
  }

  if (!config.valid) reportBlackoutConfigError();
  return config;
}

/**
 * Format a "YYYY-MM-DD" value as a full weekday + date label, e.g. "Sunday, October 25".
 * Computed in UTC so the label never shifts with the shopper's timezone.
 * @param {string} value
 * @returns {string}
 */
export function formatBlackoutDateLabel(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return String(value || '');
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-CA', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Parse a "YYYY-MM-DD" value into a UTC millisecond timestamp, or null when the value
 * is missing, malformed, or an impossible calendar date (e.g. 2026-02-30).
 * Working in UTC keeps the arithmetic independent of the shopper's timezone.
 * @param {unknown} value
 * @returns {number|null}
 */
function parseDeliveryDateValue(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  // Reject rollovers such as 2026-02-30, which Date.UTC would silently accept.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) {
    return null;
  }
  return timestamp;
}

/**
 * Format a UTC millisecond timestamp back into a "YYYY-MM-DD" date-input value.
 * @param {number} timestamp
 * @returns {string}
 */
function formatDateValue(timestamp) {
  const date = new Date(timestamp);
  return formatDateParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

/**
 * Is this whole date closed for delivery?
 * @param {string} value
 * @param {{ full: Set<string> }} [blackoutConfig]
 * @returns {boolean}
 */
export function isDeliveryBlackoutDate(value, blackoutConfig = EMPTY_BLACKOUT_CONFIG) {
  if (!value || !blackoutConfig || !blackoutConfig.full) return false;
  return blackoutConfig.full.has(value);
}

/**
 * Is this specific slot closed on this date?
 * @param {string} value
 * @param {string} slotValue
 * @param {{ slots: Map<string, { slots: Set<string> }> }} [blackoutConfig]
 * @returns {boolean}
 */
export function isDeliveryBlackoutSlot(value, slotValue, blackoutConfig = EMPTY_BLACKOUT_CONFIG) {
  if (!value || !slotValue || !blackoutConfig || !blackoutConfig.slots) return false;
  const entry = blackoutConfig.slots.get(value);
  return Boolean(entry && entry.slots.has(slotValue));
}

/**
 * Find the next bookable delivery date strictly after `afterValue`, so the shopper is
 * told a concrete date instead of being left to guess.
 *
 * A date is bookable when it is not a whole-day closure and, when the shopper has
 * already chosen a slot, that slot is not closed on it either. The search is bounded by
 * the date input's selectable window: it never returns a date below `options.min` or
 * beyond `options.max`, and returns '' when nothing in the window is bookable (the
 * caller then falls back to the generic wording, never printing a date outside the window).
 *
 * Pure client-side arithmetic on the closure config: no network, no server dependency.
 * Never throws: unreadable input simply yields no date.
 *
 * @param {string} afterValue - blocked "YYYY-MM-DD" date; the search starts the day after it
 * @param {ReturnType<typeof parseBlackoutConfig>} [blackoutConfig]
 * @param {{ slotValue?: string, min?: string, max?: string }} [options]
 * @returns {string} bookable "YYYY-MM-DD" date, or '' when none exists in the window
 */
export function getNextAvailableDeliveryDate(
  afterValue,
  blackoutConfig = EMPTY_BLACKOUT_CONFIG,
  options = {}
) {
  const afterTimestamp = parseDeliveryDateValue(afterValue);
  if (afterTimestamp === null) return '';

  const opts = options && typeof options === 'object' ? options : {};
  const maxTimestamp = parseDeliveryDateValue(opts.max);
  // Without a readable upper bound there is no window to respect; say nothing rather
  // than risk naming a date the picker will not accept.
  if (maxTimestamp === null) return '';
  const minTimestamp = parseDeliveryDateValue(opts.min);
  const slotValue = typeof opts.slotValue === 'string' ? opts.slotValue : '';
  const config = blackoutConfig || EMPTY_BLACKOUT_CONFIG;

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  for (let timestamp = afterTimestamp + ONE_DAY_MS; timestamp <= maxTimestamp; timestamp += ONE_DAY_MS) {
    if (minTimestamp !== null && timestamp < minTimestamp) continue;
    const value = formatDateValue(timestamp);
    if (isDeliveryBlackoutDate(value, config)) continue;
    if (slotValue && isDeliveryBlackoutSlot(value, slotValue, config)) continue;
    return value;
  }
  return '';
}

/**
 * Build the shopper-facing "fully booked" message for a closed date or slot,
 * or '' when the date/slot combination is bookable.
 *
 * When a concrete next bookable date exists inside the selectable window it is named;
 * otherwise the message keeps the generic "please choose another delivery date" wording.
 * @param {string} value
 * @param {ReturnType<typeof parseBlackoutConfig>} [blackoutConfig]
 * @param {{ slotValue?: string, min?: string, max?: string }} [options]
 * @returns {string}
 */
export function getBlackoutMessage(value, blackoutConfig = EMPTY_BLACKOUT_CONFIG, options = {}) {
  if (!value || !blackoutConfig) return '';
  const opts = options && typeof options === 'object' ? options : {};
  const dateLabel = formatBlackoutDateLabel(value);
  const slotValue = opts.slotValue;
  const entry = blackoutConfig.slots?.get(value);
  const bounds = { min: opts.min, max: opts.max };

  if (slotValue && entry && entry.slots.has(slotValue)) {
    const description = entry.description || slotValue;
    const head = `We're fully booked for the ${description} of ${dateLabel}.`;
    const next = getNextAvailableDeliveryDate(value, blackoutConfig, { slotValue, ...bounds });
    if (next) {
      const tail = entry.alternative ? `, or select the ${entry.alternative} slot.` : '.';
      return `${head} The next available delivery date for this time slot is ${formatBlackoutDateLabel(next)}${tail}`;
    }
    return entry.alternative
      ? `${head} Please choose another delivery date, or select the ${entry.alternative} slot.`
      : `${head} Please choose another delivery date.`;
  }

  if (blackoutConfig.full?.has(value)) {
    const head = `We're fully booked on ${dateLabel}.`;
    const next = getNextAvailableDeliveryDate(value, blackoutConfig, { ...bounds, slotValue });
    if (next) {
      return `${head} The next available delivery date is ${formatBlackoutDateLabel(next)}. Please choose that date.`;
    }
    return `${head} Please choose another delivery date.`;
  }

  return '';
}

/**
 * Validate a delivery date against bounds and whole-day closures.
 * When a true same-day date (the store's current date in its delivery timezone) is
 * submitted, surfaces a call-to-order message so the shopper knows they must phone the
 * store (no same-day online ordering). Any other out-of-range date (e.g. a future date
 * before the configured minimum lead) keeps the generic min-date message.
 * Slot-specific closures are handled separately by getBlackoutMessage so the date can
 * be kept while only the slot is rejected.
 * @param {string} value
 * @param {{ min: string, max: string }} bounds
 * @param {ReturnType<typeof parseBlackoutConfig>} [blackoutConfig]
 * @param {string} [callToOrder] - formatted phone used in the same-day notice
 * @param {Date} [now] - reference instant for the store's current date (tests may inject)
 * @param {string} [slotValue] - currently selected slot, so a whole-day closure can name
 *   the next date that is actually bookable for that slot
 * @returns {string}
 */
export function getDeliveryDateValidationMessage(
  value,
  bounds,
  blackoutConfig = EMPTY_BLACKOUT_CONFIG,
  callToOrder = '',
  now = new Date(),
  slotValue = ''
) {
  if (!value) return '';
  if (value < bounds.min) {
    const storeToday = getStoreTodayString(now, DELIVERY_TIME_ZONE);
    if (callToOrder && value <= storeToday) {
      return CALL_TO_ORDER_MESSAGE.replace('{phone}', callToOrder).replace('{min}', bounds.min);
    }
    return `Please choose a delivery date on or after ${bounds.min}.`;
  }
  if (value > bounds.max) return `Please choose a delivery date on or before ${bounds.max}.`;
  if (isDeliveryBlackoutDate(value, blackoutConfig)) {
    return getBlackoutMessage(value, blackoutConfig, {
      slotValue,
      min: bounds.min,
      max: bounds.max,
    });
  }
  return '';
}

/**
 * Normalize a raw phone value ("+1 6473237312") into a display form like "647-323-7312".
 * Keeps the number in sync with settings.ff_phone wherever it is rendered.
 * @param {string|undefined} raw
 * @returns {string}
 */
export function formatPhoneForDisplay(raw = '') {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(raw);
}

/**
 * Send non-PII delivery scheduler diagnostic events to the existing Google tag.
 * Counts intent in GA4 without exposing customer identity or address data.
 * @param {string} eventName
 * @param {Record<string, string | number | boolean | undefined>} params
 */
function emitDeliveryAnalyticsEvent(eventName, params = {}) {
  const compactParams = Object.fromEntries(
    Object.entries({
      event_source: 'cart_delivery_scheduler',
      ...params,
    }).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, compactParams);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...compactParams });
}

/**
 * Custom element for delivery date and time-slot selection in the cart.
 * Persists values as cart attributes via the Shopify cart update API.
 *
 * @typedef {Object} CartDeliverySchedulerRefs
 * @property {HTMLInputElement} deliveryDate
 * @property {HTMLSelectElement} deliverySlot
 * @property {HTMLElement} deliveryError
 */

/** @extends {Component<CartDeliverySchedulerRefs>} */
class CartDeliveryScheduler extends Component {
  requiredRefs = ['deliveryDate', 'deliverySlot', 'deliveryError'];

  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {HTMLFormElement | null} */
  #cartForm = null;
  /** @type {ReturnType<typeof parseBlackoutConfig> | null} */
  #blackoutConfig = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setDateConstraints();
    this.#rejectInvalidSavedDate();
    this.#bindEvents();
    this.#interceptCheckout();
    this.#updateConfirmation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { deliveryDate, deliverySlot } = this.refs;
    deliveryDate.removeEventListener('change', this.#handleChange);
    deliverySlot.removeEventListener('change', this.#handleChange);
    deliveryDate.removeEventListener('blur', this.#handleBlur);
    deliverySlot.removeEventListener('blur', this.#handleBlur);
    deliveryDate.removeEventListener('invalid', this.#handleInvalid);
    deliverySlot.removeEventListener('invalid', this.#handleInvalid);

    if (this.#cartForm) {
      this.#cartForm.removeEventListener('submit', this.#onFormSubmit);
      this.#cartForm = null;
    }

    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  /** Set min/max on the date input so same-day is never selectable online. */
  #setDateConstraints() {
    const { deliveryDate } = this.refs;
    const maxDays = parseInt(this.dataset.maxDays || '30', 10);
    const minLeadDays = parseInt(this.dataset.minDays || '1', 10);

    const bounds = getDeliveryDateBounds(new Date(), maxDays, minLeadDays);

    deliveryDate.min = bounds.min;
    deliveryDate.max = bounds.max;
  }

  /** Bind change handlers to both inputs. */
  #bindEvents() {
    const { deliveryDate, deliverySlot } = this.refs;
    deliveryDate.addEventListener('change', this.#handleChange);
    deliverySlot.addEventListener('change', this.#handleChange);
    deliveryDate.addEventListener('blur', this.#handleBlur);
    deliverySlot.addEventListener('blur', this.#handleBlur);
    deliveryDate.addEventListener('invalid', this.#handleInvalid);
    deliverySlot.addEventListener('invalid', this.#handleInvalid);
  }

  /** @returns {boolean} */
  #isRequired() {
    return this.dataset.required === 'true';
  }

  /**
   * Intercept checkout form submission for validation when required.
   * Hooks into the cart form's submit event to block checkout if fields are empty or invalid.
   */
  #interceptCheckout() {
    if (this.dataset.required !== 'true') return;

    const form = document.getElementById('cart-form');
    if (!form) return;
    this.#cartForm = form;
    this.#cartForm.addEventListener('submit', this.#onFormSubmit);
  }

  /** @param {SubmitEvent} event */
  #onFormSubmit = (event) => {
    const { deliveryDate, deliverySlot } = this.refs;
    this.classList.add('is-submitted');
    deliveryDate.classList.add('is-touched');
    deliverySlot.classList.add('is-touched');
    const dateError = this.#getDateValidationMessage();
    const slotError = dateError ? '' : this.#getSlotValidationMessage();
    const hasMissingFields = !deliveryDate.value || !deliverySlot.value;

    if (dateError || slotError || hasMissingFields) {
      event.preventDefault();
      const blockedEvent = dateError
        ? 'ff_checkout_blocked_invalid_date'
        : slotError
          ? 'ff_checkout_blocked_invalid_slot'
          : 'ff_checkout_blocked_delivery_missing';
      emitDeliveryAnalyticsEvent(blockedEvent, {
        missing_delivery_date: !deliveryDate.value,
        missing_delivery_slot: !deliverySlot.value,
        delivery_date_error: dateError || undefined,
        delivery_slot_error: slotError || undefined,
        delivery_date_selected: Boolean(deliveryDate.value),
        delivery_slot_selected: Boolean(deliverySlot.value),
      });
      this.#showError(dateError || slotError || 'Please select a delivery date and time slot.');
      deliveryDate.setCustomValidity(dateError);
      deliverySlot.setCustomValidity(slotError);
      deliveryDate.classList.toggle('is-invalid', Boolean(dateError) || !deliveryDate.value);
      deliverySlot.classList.toggle('is-invalid', Boolean(slotError) || !deliverySlot.value);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    this.#clearError();
    return true;
  };

  /**
   * Handle changes to date or time slot.
   * Selection analytics are emitted synchronously so rapid date + slot changes are not coalesced
   * by the debounced cart attribute persistence request.
   * @param {Event} event
   */
  #handleChange = (event) => {
    this.#emitSelectionAnalytics(event);
    this.#persistChangedDeliveryAttributes(event);
  };

  /** @param {Event} event */
  #emitSelectionAnalytics(event) {
    const { deliveryDate, deliverySlot } = this.refs;
    const dateError = this.#getDateValidationMessage();
    const changedField = event.target === deliveryDate ? 'date' : event.target === deliverySlot ? 'slot' : 'unknown';

    if (changedField === 'date') {
      emitDeliveryAnalyticsEvent('ff_delivery_date_selected', {
        delivery_date_selected: Boolean(deliveryDate.value),
        delivery_date_is_valid: Boolean(deliveryDate.value && !dateError),
        delivery_slot_selected: Boolean(deliverySlot.value),
      });
    } else if (changedField === 'slot') {
      emitDeliveryAnalyticsEvent('ff_delivery_slot_selected', {
        delivery_date_selected: Boolean(deliveryDate.value),
        delivery_slot_selected: Boolean(deliverySlot.value),
        delivery_slot_value: deliverySlot.value || undefined,
      });
    }
  }

  /**
   * Persist delivery attributes after date/slot changes.
   * Debounced to avoid rapid-fire API calls when the user is clicking through dates.
   * @param {Event} event
   */
  #persistChangedDeliveryAttributes = debounce(async (event) => {
    const { deliveryDate, deliverySlot } = this.refs;

    this.#clearError();
    event.target?.classList?.add('is-touched');

    const dateError = this.#getDateValidationMessage();

    if (dateError) {
      deliveryDate.value = '';
      if (this.#isRequired()) {
        deliveryDate.setCustomValidity(dateError);
      }
      deliveryDate.classList.add('is-touched', 'is-invalid');
      this.#showError(dateError);
      const persisted = await this.#persistDeliveryAttributes(
        {
          'Delivery Date': '',
          'Delivery Time Slot': deliverySlot.value,
        },
        event
      );
      if (persisted) {
        this.#updateConfirmation();
      }
      return;
    }

    // Slot-only closure: keep the date (so the shopper can switch slots), reject the
    // slot, and leave the last valid cart attributes untouched until a valid pick exists.
    const slotError = this.#getSlotValidationMessage();
    if (slotError) {
      if (this.#isRequired()) {
        deliverySlot.setCustomValidity(slotError);
      }
      deliverySlot.classList.add('is-touched', 'is-invalid');
      this.#showError(slotError);
      return;
    }

    if (this.classList.contains('is-submitted') && this.#isRequired()) {
      deliveryDate.classList.toggle('is-invalid', !deliveryDate.value);
      deliverySlot.classList.toggle('is-invalid', !deliverySlot.value);
      if (!deliveryDate.value || !deliverySlot.value) {
        this.#showError('Please select a delivery date and time slot.');
      }
    }

    const persisted = await this.#persistDeliveryAttributes(
      {
        'Delivery Date': deliveryDate.value,
        'Delivery Time Slot': deliverySlot.value,
      },
      event
    );
    if (persisted) {
      this.#updateConfirmation();
      if (deliveryDate.value && deliverySlot.value) {
        emitDeliveryAnalyticsEvent('ff_delivery_selection_saved', {
          delivery_date_selected: true,
          delivery_slot_selected: true,
          delivery_slot_value: deliverySlot.value,
        });
      }
    }
  }, 300);

  /** Clear a saved date that has become invalid (e.g. it is now today) since the cart was last opened. */
  #rejectInvalidSavedDate() {
    const { deliveryDate, deliverySlot } = this.refs;
    const dateError = this.#getDateValidationMessage();

    if (dateError) {
      deliveryDate.value = '';
      if (this.#isRequired()) {
        deliveryDate.setCustomValidity(dateError);
      }
      this.#persistDeliveryAttributes({
        'Delivery Date': '',
        'Delivery Time Slot': deliverySlot.value,
      });
      return;
    }

    // A saved date whose chosen slot is now closed: keep the date, flag the slot so
    // checkout stays blocked until a bookable slot is picked.
    const slotError = this.#getSlotValidationMessage();
    if (slotError) {
      if (this.#isRequired()) {
        deliverySlot.setCustomValidity(slotError);
      }
      deliverySlot.classList.add('is-touched', 'is-invalid');
      this.#showError(slotError);
    }
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();
    const { deliveryDate, deliverySlot } = this.refs;
    this.classList.add('is-submitted');
    deliveryDate.classList.add('is-touched');
    deliverySlot.classList.add('is-touched');

    const dateError = this.#getDateValidationMessage();
    const slotError = dateError ? '' : this.#getSlotValidationMessage();
    deliveryDate.classList.toggle('is-invalid', Boolean(dateError) || !deliveryDate.value);
    deliverySlot.classList.toggle('is-invalid', Boolean(slotError) || !deliverySlot.value);
    this.#showError(dateError || slotError || 'Please select a delivery date and time slot.');
  };

  /** @param {FocusEvent} event */
  #handleBlur = (event) => {
    const { deliveryDate, deliverySlot } = this.refs;
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;

    field.classList.add('is-touched');
    if (!this.#isRequired() && field !== deliveryDate) return;

    const dateError = this.#getDateValidationMessage();
    const slotError = dateError ? '' : this.#getSlotValidationMessage();
    const missingDate = this.#isRequired() && !deliveryDate.value;
    const missingSlot = this.#isRequired() && !deliverySlot.value;

    deliveryDate.classList.toggle('is-invalid', Boolean(dateError) || missingDate);
    deliverySlot.classList.toggle('is-invalid', Boolean(slotError) || missingSlot);

    if (dateError) {
      deliveryDate.setCustomValidity(dateError);
      this.#showError(dateError);
    } else if (slotError) {
      deliverySlot.setCustomValidity(slotError);
      this.#showError(slotError);
    } else if (field === deliveryDate && missingDate) {
      this.#showError('Please select a delivery date.');
    } else if (field === deliverySlot && missingSlot) {
      this.#showError('Please select a delivery time slot.');
    } else if (!missingDate && !missingSlot) {
      this.#clearError();
    }
  };

  /**
   * Persist scheduler attributes without touching other cart attributes.
   * @param {Record<string, string>} attributes
   * @param {Event} [event]
   * @returns {Promise<boolean>}
   */
  async #persistDeliveryAttributes(attributes, event) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Cart attribute update failed (${response.status})`);
      }

      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return false;
      console.error('Failed to update delivery attributes', error);
      return false;
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
      if (event) {
        cartPerformance.measureFromEvent('delivery-scheduler:user-action', event);
      }
    }
  }

  /** Update the confirmation summary line below the fields. */
  #updateConfirmation() {
    const { deliveryDate, deliverySlot } = this.refs;
    const confirm = this.querySelector('.cart-delivery-scheduler__confirm');
    const confirmText = this.querySelector('.cart-delivery-scheduler__confirm-text');
    if (!confirm || !confirmText) return;

    if (deliveryDate.value && deliverySlot.value) {
      const date = new Date(deliveryDate.value + 'T12:00:00');
      const formattedDate = date.toLocaleDateString('en-CA', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });
      const slotLabel = deliverySlot.options[deliverySlot.selectedIndex]?.text ?? deliverySlot.value;
      confirmText.textContent = `${formattedDate} · ${slotLabel}`;
      confirm.removeAttribute('hidden');
    } else {
      confirm.setAttribute('hidden', '');
    }
  }

  /**
   * Read and cache the storefront closure config from its data attributes.
   * Cached because the attributes are rendered once and never change on the page.
   * @returns {ReturnType<typeof parseBlackoutConfig>}
   */
  #getBlackoutConfig() {
    if (!this.#blackoutConfig) {
      this.#blackoutConfig = parseBlackoutConfig(this.dataset.blackoutFull, this.dataset.blackoutSlots);
    }
    return this.#blackoutConfig;
  }

  /**
   * Message for a date that is closed for the whole day, or '' when bookable.
   * @returns {string}
   */
  #getDateValidationMessage() {
    this.#setDateConstraints();

    const { deliveryDate, deliverySlot } = this.refs;
    return getDeliveryDateValidationMessage(
      deliveryDate.value,
      { min: deliveryDate.min, max: deliveryDate.max },
      this.#getBlackoutConfig(),
      formatPhoneForDisplay(this.dataset.callPhone),
      new Date(),
      deliverySlot.value
    );
  }

  /**
   * Message for a slot that is closed on the selected date, or '' when bookable.
   * Only meaningful once a date is chosen; the date itself is kept so the shopper
   * can switch to another slot without re-picking the day.
   * @returns {string}
   */
  #getSlotValidationMessage() {
    const { deliveryDate, deliverySlot } = this.refs;
    return getBlackoutMessage(deliveryDate.value, this.#getBlackoutConfig(), {
      slotValue: deliverySlot.value,
      min: deliveryDate.min,
      max: deliveryDate.max,
    });
  }

  /** @param {string} message */
  #showError(message) {
    const { deliveryError } = this.refs;
    const errorText = deliveryError.querySelector('.cart-delivery-scheduler__error-text');
    if (errorText) {
      errorText.textContent = message;
    }
    deliveryError.classList.remove('hidden');
  }

  #clearError() {
    const { deliveryDate, deliverySlot, deliveryError } = this.refs;
    deliveryError.classList.add('hidden');
    deliveryDate.setCustomValidity('');
    deliverySlot.setCustomValidity('');
    deliveryDate.classList.remove('is-invalid');
    deliverySlot.classList.remove('is-invalid');
  }
}

if (!customElements.get('cart-delivery-scheduler')) {
  customElements.define('cart-delivery-scheduler', CartDeliveryScheduler);
}